import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Users, Plus, RefreshCw, Eye, EyeOff, Calendar, Mail, AtSign, ShieldCheck, User, KeyRound, Send, BadgeCheck, Globe } from "lucide-react";
import UserAvatar from "../../components/UserAvatar";
import ImageEditModal from "../../components/admin/ImageEditModal";
import MediaPickerModal from "./MediaPickerModal";
import { SINGLE_UPLOAD_MAX_BYTES, TOO_LARGE_SINGLE_UPLOAD_MESSAGE } from "../../lib/multipartUpload";
import {
  API,
  getAdminSessionToken,
  useToast,
  ConfirmModal,
  Modal,
  Field,
  iCls,
  SaveButton,
  PageHeader,
  TableCard,
  TableHead,
  TableSkeleton,
  EditBtn,
  DeleteBtn,
  SearchBar,
  PrimaryBtn,
  EmptyState,
} from "./_shared";
import { useAuth } from "../../contexts/AuthContext";

const emptyForm = {
  fullName: "",
  email: "",
  username: "",
  role: "user",
  adminRole: "editor",
  avatar: "",
  emailVerified: true,
  authProvider: "local",
  googleId: "",
  // create-only
  password: "",
};

const ADMIN_ROLE_LABEL = { owner: "Chủ sở hữu", reviewer: "Biên tập duyệt", editor: "Biên tập viên", viewer: "Chỉ xem" };
const ADMIN_ROLE_TONE = {
  owner: "bg-violet-50 text-violet-700 ring-violet-200",
  reviewer: "bg-sky-50 text-sky-700 ring-sky-200",
  editor: "bg-amber-50 text-amber-700 ring-amber-200",
  viewer: "bg-slate-100 text-slate-700 ring-slate-200",
};

const RoleBadge = ({ role, adminRole }) => role === "admin" ? (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${ADMIN_ROLE_TONE[adminRole || "owner"]}`}>
    <ShieldCheck size={11} />
    {ADMIN_ROLE_LABEL[adminRole || "owner"]}
  </span>
) : (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
    <User size={11} />
    User
  </span>
);

const AdminUsers = () => {
  const { token: authUserToken } = useAuth();
  const { push: toast, ToastPortal } = useToast();

  const [activeTab, setActiveTab] = useState("info"); // info | security | favorites
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const { user: me } = useAuth();
  const [rolesInfo, setRolesInfo] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPwd, setShowPwd] = useState(false);
  const [noAuth, setNoAuth] = useState(false);
  const [pwdDraft, setPwdDraft] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [securityExpanded, setSecurityExpanded] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [, setAvatarEditFile] = useState(null);
  const [pendingCreateAvatar, setPendingCreateAvatar] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);

  const setter = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: val }));
  };

  /** Token phiên đang đăng nhập (context) trước, rồi localStorage — không ưu tiên adminToken cũ trước authToken. */
  const getToken = useCallback(
    () => authUserToken || getAdminSessionToken(),
    [authUserToken]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setNoAuth(false);
    try {
      // API phân trang (mặc định 20/trang) — lấy lần lượt các trang để danh sách, tìm kiếm và đếm không bị cụt
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` };
      const all = [];
      let page = 1;
      let pages = 1;
      do {
        const res = await window.fetch(`${API}/users?page=${page}&limit=200`, { headers });
        if (res.status === 401 || res.status === 403) { setNoAuth(true); return; }
        const data = await res.json();
        if (Array.isArray(data)) { all.push(...data); break; }
        all.push(...(data?.users || data?.data || []));
        pages = data?.pagination?.pages || 1;
        page += 1;
      } while (page <= pages && page <= 25);
      setUsers(all);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [getToken, toast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Mô tả các vai trò quản trị + số người mỗi vai trò
  useEffect(() => {
    window.fetch(`${API}/audit/roles`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setRolesInfo(d.roles || []))
      .catch(() => {});
  }, [getToken]);

  const apiFetchAuth = useCallback(async (url, opts = {}) => {
    const res = await window.fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      ...opts,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Lỗi từ server");
    return data;
  }, [getToken]);

  const openAdd = () => { setForm(emptyForm); setDrawerMode("add"); setEditUser(null); setShowPwd(false); setDrawerOpen(true); };
  const openEdit = (u) => {
    setForm({
      fullName: u.fullName || "",
      email: u.email || "",
      username: u.username || "",
      role: u.role || "user",
      adminRole: u.adminRole || (u.role === "admin" ? "owner" : "editor"),
      avatar: u.avatar || "",
      emailVerified: Boolean(u.emailVerified),
      authProvider: u.authProvider || "local",
      googleId: u.googleId || "",
      password: "",
    });
    setDrawerMode("edit");
    setEditUser(u);
    setShowPwd(false);
    setPwdDraft("");
    setAvatarFile(null);
    setAvatarEditFile(null);
    setPendingCreateAvatar(null);
    setAvatarPickerOpen(false);
    setActiveTab("info");
    setSecurityExpanded(false);
    setDrawerOpen(true);
  };

  const closeModal = () => {
    setDrawerOpen(false);
    setSecurityExpanded(false);
    setActiveTab("info");
    setAvatarFile(null);
    setAvatarEditFile(null);
    setPendingCreateAvatar(null);
    setAvatarPickerOpen(false);
  };

  const uploadAvatarForUser = async (file, userIdOverride) => {
    const targetUserId = userIdOverride || editUser?._id;
    if (!targetUserId) return;
    if (!file) return toast("Vui lòng chọn ảnh trước", "error");
    if (Number(file.size || 0) > SINGLE_UPLOAD_MAX_BYTES) return toast(TOO_LARGE_SINGLE_UPLOAD_MESSAGE, "error");
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const res = await window.fetch(`${API}/users/${targetUserId}/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Lỗi upload avatar");
      const updated = data?.data || data?.user || data;
      if (updated?.avatar) {
        setForm((p) => ({ ...p, avatar: updated.avatar }));
        setEditUser((p) => ({ ...(p || {}), avatar: updated.avatar }));
      }
      setAvatarFile(null);
      setAvatarEditFile(null);
      toast(data?.message || "Đã upload avatar", "success");
      loadUsers();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (drawerMode === "edit") {
        const payload = {
          fullName: form.fullName.trim(),
          role: form.role,
          avatar: (form.avatar || "").trim(),
          emailVerified: Boolean(form.emailVerified),
          authProvider: form.authProvider,
        };
        if (form.role === "user") payload.email = (form.email || "").trim();
        if (form.role === "admin") {
          payload.username = (form.username || "").trim();
          payload.adminRole = form.adminRole;
        }
        await apiFetchAuth(`${API}/users/${editUser._id}`, { method: "PUT", body: JSON.stringify(payload) });
        toast("Cập nhật tài khoản thành công!", "success");
      } else {
        const payload = {
          email: (form.email || "").trim(),
          username: (form.username || "").trim(),
          fullName: form.fullName.trim(),
          password: form.password,
          role: "user",
          avatar: (form.avatar || "").trim(),
          emailVerified: true,
          authProvider: "local",
        };
        const created = await apiFetchAuth(`${API}/users`, { method: "POST", body: JSON.stringify(payload) });
        const createdUser = created?.data || created?.user || created;
        const newId = createdUser?._id;
        if (pendingCreateAvatar && newId) {
          await uploadAvatarForUser(pendingCreateAvatar, newId);
        }
        toast("Tạo tài khoản mới thành công!", "success");
      }
      setDrawerOpen(false);
      loadUsers();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const favoritesPreview = useMemo(() => {
    const favs = editUser?.favorites;
    if (!favs) return { count: 0, names: [] };
    if (Array.isArray(favs)) {
      const names = favs.map((f) => f?.name).filter(Boolean).slice(0, 8);
      return { count: favs.length, names };
    }
    return { count: 0, names: [] };
  }, [editUser]);

  const handleAdminSetPassword = async () => {
    if (!editUser?._id) return;
    setChangingPwd(true);
    try {
      await apiFetchAuth(`${API}/users/${editUser._id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ newPassword: pwdDraft }),
      });
      toast("Đã cập nhật mật khẩu!", "success");
      setPwdDraft("");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setChangingPwd(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!editUser?._id) return;
    setSendingReset(true);
    try {
      const res = await apiFetchAuth(`${API}/users/${editUser._id}/system-reset-password`, {
        method: "POST",
      });
      toast(res?.message || "Đã cấp mật khẩu tạm ngẫu nhiên và gửi email — người dùng phải đổi khi đăng nhập", "success");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSendingReset(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirm(null);
    try {
      await apiFetchAuth(`${API}/users/${id}`, { method: "DELETE" });
      toast("Đã xoá tài khoản!", "success");
      loadUsers();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchQ = !q || u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q);
    return matchQ && (!filterRole || u.role === filterRole);
  });

  const adminCount = users.filter((u) => u.role === "admin").length;
  const userCount = users.filter((u) => u.role === "user").length;

  if (noAuth) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 text-amber-600">
        <ShieldCheck size={28} strokeWidth={1.5} />
        <p className="font-semibold">Không có quyền truy cập</p>
        <p className="text-sm text-amber-500">Vai trò của bạn chưa được cấp quyền quản lý tài khoản. Hãy liên hệ chủ sở hữu hệ thống.</p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* Header — branding: Faustina tiêu đề, DarleySans nội dung (font-text từ AdminLayout) */}
      <PageHeader
        title="Quản lý người dùng"
        titleClassName="text-3xl font-extrabold tracking-tight text-admin-text md:text-4xl"
        subtitle={`${users.length} tài khoản · ${adminCount} admin · ${userCount} user`}
        action={
          <>
            <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm người dùng..." />
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
              className="rounded-xl border border-admin-border bg-admin-surface px-3 py-2.5 text-sm text-admin-text outline-none shadow-sm transition focus:border-admin-primary focus:ring-2 focus:ring-[rgba(37,106,191,0.14)]">
              <option value="">Tất cả role</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <button onClick={loadUsers}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:border-admin-primary/25 hover:bg-admin-primaryLight hover:text-admin-primary active:scale-95">
              <RefreshCw size={15} />
            </button>
            <PrimaryBtn onClick={openAdd} icon={Plus}>Thêm User</PrimaryBtn>
          </>
        }
      />

      {/* Table */}
      <TableCard>
        {loading ? (
          <TableSkeleton cols={5} rows={7} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Users} title="Không có người dùng nào"
            description={search ? "Thử tìm kiếm với từ khoá khác" : "Nhấn 'Thêm User' để tạo tài khoản đầu tiên"} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <TableHead cols={["Người dùng", "Liên hệ", "Role", "Ngày tham gia", ""]} />
              <tbody className="divide-y divide-admin-border">
                {filtered.map((u) => (
                  <tr key={u._id} className="transition-colors hover:bg-admin-muted/60">
                    {/* Name + avatar */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <UserAvatar url={u.avatar} name={u.fullName} sizeClass="h-9 w-9" textClass="text-xs" roundedClass="rounded-full" />
                        <div>
                          <p className="font-semibold text-admin-text">{u.fullName}</p>
                          <p className="text-[11px] text-admin-textMuted">#{u._id?.slice(-6)}</p>
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="px-5 py-3.5">
                      {u.email && (
                        <div className="flex items-center gap-1.5 text-xs text-admin-textMuted">
                          <Mail size={11} className="shrink-0 text-admin-textMuted" />{u.email}
                        </div>
                      )}
                      {u.username && (
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-admin-textMuted">
                          <AtSign size={11} className="shrink-0 text-admin-textMuted" />{u.username}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {u.emailVerified ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                            <BadgeCheck size={11} /> Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-admin-muted px-2 py-0.5 text-[10px] font-bold text-admin-textMuted ring-1 ring-admin-border">
                            <BadgeCheck size={11} /> Unverified
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full bg-admin-muted px-2 py-0.5 text-[10px] font-bold text-admin-text ring-1 ring-admin-border">
                          <Globe size={11} /> {u.authProvider || "local"}
                        </span>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-5 py-3.5"><RoleBadge role={u.role} adminRole={u.adminRole} /></td>

                    {/* Date */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5 text-xs text-admin-textMuted">
                        <Calendar size={11} />
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString("vi-VN") : "—"}
                      </div>
                    </td>

                    {/* Actions — luôn hiện, màu rõ */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <EditBtn variant="solid" onClick={() => openEdit(u)} />
                        <DeleteBtn variant="solid" onClick={() => setConfirm(u._id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </TableCard>

      {/* Form modal (centered) */}
      <Modal
        open={drawerOpen}
        onClose={closeModal}
        title={drawerMode === "add" ? "Thêm tài khoản mới" : "Chỉnh sửa tài khoản"}
        maxWidthClass="max-w-4xl"
      >
        <form onSubmit={handleSave} className="flex flex-col gap-0">
          {avatarPickerOpen && (
            <MediaPickerModal
              open={avatarPickerOpen}
              onClose={() => setAvatarPickerOpen(false)}
              title="Chọn avatar từ S3"
              initialPrefix="users/"
              accept="image"
              onSelect={(file) => {
                setAvatarPickerOpen(false);
                setPendingCreateAvatar(null);
                setForm((p) => ({ ...p, avatar: file.url }));
                toast("Đã chọn avatar từ thư viện", "success");
              }}
            />
          )}
          {/* Image edit modal (shared) */}
          {avatarFile && (
            <ImageEditModal
              title="Chỉnh ảnh đại diện"
              file={avatarFile}
              shape="round"
              onCancel={() => setAvatarFile(null)}
              onConfirm={async (processedFile) => {
                if (drawerMode === "add") {
                  setPendingCreateAvatar(processedFile);
                  setAvatarFile(null);
                  toast("Đã chọn avatar. Bấm Lưu để tạo user.", "success");
                  return;
                }
                await uploadAvatarForUser(processedFile);
                setAvatarFile(null);
              }}
            />
          )}

          {/* Tabs (edit only) */}
          {drawerMode === "edit" && (
            <div className="mb-6 rounded-2xl border border-admin-border bg-admin-muted/60 p-3 shadow-sm">
              <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("info")}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition shadow-sm ${
                  activeTab === "info"
                    ? "bg-admin-primary text-white"
                    : "bg-admin-surface text-admin-text ring-1 ring-admin-border hover:bg-admin-muted"
                }`}
              >
                Thông tin
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("security")}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition shadow-sm ${
                  activeTab === "security"
                    ? "bg-admin-primary text-white"
                    : "bg-admin-surface text-admin-text ring-1 ring-admin-border hover:bg-admin-muted"
                }`}
              >
                Bảo mật
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("favorites")}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition shadow-sm ${
                  activeTab === "favorites"
                    ? "bg-admin-primary text-white"
                    : "bg-admin-surface text-admin-text ring-1 ring-admin-border hover:bg-admin-muted"
                }`}
              >
                Yêu thích
              </button>
            </div>
            </div>
          )}

          {/* INFO TAB: layout 2 columns; fields in 2-per-row grid */}
          {(drawerMode === "add" || activeTab === "info") ? (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Header card spans full width */}
              {drawerMode === "edit" && editUser && (
                <div className="md:col-span-2 rounded-3xl border border-admin-border bg-admin-muted/60 p-4 shadow-sm">
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        const url = (form.avatar || editUser.avatar || "").trim();
                        if (!url) return;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      className="shrink-0 rounded-full ring-2 ring-white shadow-sm transition hover:shadow-md"
                      title="Mở avatar ở tab mới"
                    >
                      <UserAvatar url={form.avatar || editUser.avatar} name={form.fullName || editUser.fullName} sizeClass="h-14 w-14" textClass="text-sm" roundedClass="rounded-full" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-admin-text">{editUser.fullName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <RoleBadge role={editUser.role} adminRole={editUser.adminRole} />
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                          <BadgeCheck size={11} /> {editUser.emailVerified ? "Verified" : "Unverified"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-admin-surface px-2 py-0.5 text-[10px] font-bold text-admin-text ring-1 ring-admin-border">
                          <Globe size={11} /> {editUser.authProvider || "local"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-admin-surface px-2 py-0.5 text-[10px] font-bold text-admin-textMuted ring-1 ring-admin-border">
                          <Calendar size={11} /> {editUser.createdAt ? new Date(editUser.createdAt).toLocaleDateString("vi-VN") : "—"}
                        </span>
                      </div>
                    </div>
                    <div className="hidden sm:block text-right">
                      <p className="text-[11px] font-bold text-admin-textMuted">ID</p>
                      <p className="font-mono text-[11px] text-admin-textMuted">{editUser._id?.slice(-10)}</p>
                    </div>
                  </div>

                  {/* Avatar update controls */}
                  <div className="mt-4 flex flex-col gap-3">
                    <p className="text-xs font-semibold text-admin-textMuted">Cập nhật ảnh đại diện</p>
                    <button
                      type="button"
                      onClick={() => setAvatarPickerOpen(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-admin-border bg-admin-surface px-4 py-2.5 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted"
                    >
                      Chọn từ thư viện
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      className={iCls}
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setAvatarEditFile(f);
                        if (f) setAvatarFile(f); // mở ImageEditModal liền
                      }}
                    />
                    <p className="text-xs font-semibold text-admin-textMuted">
                      {uploadingAvatar ? "Đang upload…" : "Chọn ảnh để mở màn hình chỉnh & upload."}
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-admin-textMuted">Mẹo: nhấn vào avatar phía trên để mở ảnh ở tab mới.</p>
                </div>
              )}

              {drawerMode === "add" && (
                <div className="md:col-span-2 rounded-3xl border border-admin-border bg-admin-surface p-5 shadow-sm">
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted">Tạo tài khoản</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field label="Email" required>
                      <input
                        type="email"
                        className={iCls}
                        value={form.email}
                        onChange={setter("email")}
                        placeholder="example@email.com"
                        required
                      />
                    </Field>
                    <Field label="Username" hint="Có thể để trống (user thường).">
                      <input
                        className={iCls}
                        value={form.username}
                        onChange={setter("username")}
                        placeholder="Nhập username"
                      />
                    </Field>
                    <Field label="Mật khẩu" required>
                      <div className="relative">
                        <input
                          type={showPwd ? "text" : "password"}
                          className={`${iCls} pr-12`}
                          value={form.password}
                          onChange={setter("password")}
                          placeholder="Nhập mật khẩu"
                          required
                        />
                        <button type="button" onClick={() => setShowPwd(!showPwd)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600">
                          {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </Field>
                    <Field label="Họ và tên" required>
                      <input
                        className={iCls}
                        value={form.fullName}
                        onChange={setter("fullName")}
                        placeholder="Nhập họ và tên đầy đủ"
                        required
                      />
                    </Field>
                  </div>

                  <div className="mt-4 rounded-2xl border border-admin-border bg-admin-muted/50 p-4">
                    <p className="text-xs font-semibold text-admin-text">Avatar</p>
                    <div className="mt-2 flex flex-col gap-3">
                      {form.avatar ? (
                        <div className="flex items-center gap-3 rounded-2xl border border-admin-border bg-admin-surface p-3">
                          <UserAvatar url={form.avatar} name={form.fullName || "U"} sizeClass="h-10 w-10" textClass="text-xs" roundedClass="rounded-full" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-admin-text">Đã chọn từ thư viện</p>
                            <p className="truncate font-mono text-[10px] text-admin-textMuted" title={form.avatar}>{form.avatar}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, avatar: "" }))}
                            className="rounded-xl border border-admin-border bg-admin-surface px-3 py-2 text-xs font-bold text-admin-text hover:bg-admin-muted"
                          >
                            Bỏ chọn
                          </button>
                        </div>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => setAvatarPickerOpen(true)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-admin-border bg-admin-surface px-4 py-3 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted"
                      >
                        Chọn từ thư viện
                      </button>
                      <input
                        type="file"
                        accept="image/*"
                        className={iCls}
                        onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
                      />
                      {pendingCreateAvatar ? (
                        <p className="text-xs font-semibold text-emerald-700">Đã chọn ảnh (đã crop). Sẽ upload sau khi bấm Lưu.</p>
                      ) : (
                        <p className="text-xs text-admin-textMuted">Chọn ảnh để chỉnh/crop trước khi upload.</p>
                      )}
                      <p className="text-xs text-admin-textMuted">Email mặc định: đã xác minh.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* RIGHT */}
              {drawerMode === "edit" ? <div className="space-y-4" /> : null}

              {/* FULL WIDTH fields: 2 per row */}
              {drawerMode === "edit" && (
              <div className="md:col-span-2 rounded-3xl border border-admin-border bg-admin-surface p-5 shadow-sm">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted">Thông tin</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Họ tên" required>
                    <input className={iCls} value={form.fullName} onChange={setter("fullName")}
                      placeholder="Nhập họ và tên đầy đủ" required />
                  </Field>

                  <Field label="Role" required>
                    <select className={iCls} value={form.role} onChange={setter("role")}>
                      <option value="user">👤 User</option>
                      <option value="admin">🛡 Admin</option>
                    </select>
                  </Field>

                  {form.role === "admin" ? (
                    <Field label="Vai trò quản trị" hint={rolesInfo.find((r) => r.key === form.adminRole)?.description || "Quyết định admin này được làm gì."}>
                      <select
                        className={iCls}
                        value={form.adminRole}
                        onChange={setter("adminRole")}
                        disabled={drawerMode === "edit" && String(editUser?._id) === String(me?.id || me?._id)}
                        title={drawerMode === "edit" && String(editUser?._id) === String(me?.id || me?._id) ? "Không thể tự đổi vai trò của chính mình" : undefined}
                      >
                        {["owner", "reviewer", "editor", "viewer"].map((k) => (
                          <option key={k} value={k}>
                            {ADMIN_ROLE_LABEL[k]}
                            {rolesInfo.find((r) => r.key === k) ? ` — ${rolesInfo.find((r) => r.key === k).members} người` : ""}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}

                  <Field label="Email" required={form.role === "user"} hint={form.role === "admin" ? "Admin có thể để trống email." : ""}>
                    <input
                      type="email"
                      className={iCls}
                      value={form.email}
                      onChange={setter("email")}
                      placeholder="example@email.com"
                      required={form.role === "user"}
                    />
                  </Field>

                  <Field label="Username" required={form.role === "admin"} hint={form.role === "user" ? "User có thể để trống username." : ""}>
                    <input
                      className={iCls}
                      value={form.username}
                      onChange={setter("username")}
                      placeholder="Nhập username"
                      required={form.role === "admin"}
                    />
                  </Field>
                </div>
              </div>
              )}

              {/* Status moved to bottom */}
              {drawerMode === "edit" && (
              <div className="md:col-span-2 rounded-3xl border border-admin-border bg-admin-surface p-5 shadow-sm">
                <p className="text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted">Trạng thái</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Email verified">
                    <label className="flex items-center gap-2 rounded-2xl border border-admin-border bg-admin-surface px-3.5 py-2.5 text-sm font-semibold text-admin-text shadow-sm">
                      <input type="checkbox" checked={!!form.emailVerified} onChange={setter("emailVerified")} />
                      <span>Đã xác minh</span>
                    </label>
                  </Field>
                  <Field label="Auth provider">
                    <select className={iCls} value={form.authProvider} onChange={setter("authProvider")}>
                      <option value="local">local</option>
                      <option value="google">google</option>
                    </select>
                  </Field>
                  {drawerMode === "edit" && (
                    <Field label="Ngày tham gia">
                      <input
                        className={iCls}
                        value={editUser?.createdAt ? new Date(editUser.createdAt).toLocaleString("vi-VN") : "—"}
                        readOnly
                      />
                    </Field>
                  )}
                  {drawerMode === "edit" && (
                    <Field label="User ID">
                      <input className={`${iCls} font-mono`} value={editUser?._id || "—"} readOnly />
                    </Field>
                  )}
                </div>
              </div>
              )}

            </div>
          ) : (
            <div className="space-y-4">
              {activeTab === "security" && (
                <div className="rounded-3xl border border-admin-border bg-admin-surface p-5 shadow-sm">
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted">Bảo mật</p>
                  <div className="mt-3 space-y-3">
                    {!securityExpanded ? (
                      <button
                        type="button"
                        onClick={() => setSecurityExpanded(true)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-admin-primary px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-admin-primaryHover"
                      >
                        <KeyRound size={15} />
                        Đổi mật khẩu
                      </button>
                    ) : (
                      <>
                        <Field label="Admin tự đổi mật khẩu" hint="Nhập mật khẩu mới (tối thiểu 8 ký tự).">
                          <div className="relative">
                            <input
                              type={showPwd ? "text" : "password"}
                              className={`${iCls} pr-12`}
                              value={pwdDraft}
                              onChange={(e) => setPwdDraft(e.target.value)}
                              placeholder="••••••••"
                            />
                            <button type="button" onClick={() => setShowPwd(!showPwd)}
                              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-admin-textMuted transition hover:text-admin-text">
                              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </Field>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={changingPwd || !pwdDraft.trim()}
                            onClick={handleAdminSetPassword}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-admin-primary px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-admin-primaryHover disabled:opacity-50"
                          >
                            <KeyRound size={15} />
                            {changingPwd ? "Đang đổi…" : "Cập nhật mật khẩu"}
                          </button>
                          <button
                            type="button"
                            disabled={sendingReset || !editUser?.email}
                            onClick={handleSendResetEmail}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-admin-border bg-admin-surface px-4 py-3 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted disabled:opacity-50"
                            title={!editUser?.email ? "User không có email" : "Hệ thống đặt lại mật khẩu và gửi email"}
                          >
                            <Send size={15} />
                            {sendingReset ? "Đang gửi…" : "Tự động tạo mật khẩu mới"}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setSecurityExpanded(false); setPwdDraft(""); }}
                          className="text-xs font-bold text-admin-textMuted hover:text-admin-text"
                        >
                          Thu gọn
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "favorites" && (
                <div className="rounded-3xl border border-admin-border bg-admin-surface p-5 shadow-sm">
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted">Yêu thích (chỉ xem)</p>
                  <p className="mt-1 text-sm font-extrabold text-admin-text">{favoritesPreview.count} địa điểm</p>
                  {favoritesPreview.names.length > 0 ? (
                    <ul className="mt-3 space-y-2">
                      {favoritesPreview.names.map((n) => (
                        <li key={n} className="flex items-center justify-between rounded-2xl border border-admin-border bg-admin-muted/40 px-3 py-2 text-sm font-semibold text-admin-text">
                          <span className="truncate">{n}</span>
                        </li>
                      ))}
                      {favoritesPreview.count > favoritesPreview.names.length && (
                        <li className="text-xs font-semibold text-admin-textMuted">…</li>
                      )}
                    </ul>
                  ) : (
                    <p className="mt-2 text-xs text-admin-textMuted">Chưa có yêu thích.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex gap-3 border-t border-admin-border pt-4">
            <button type="button" onClick={closeModal}
              className="flex-1 rounded-2xl border border-admin-border bg-admin-surface py-3 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted">
              Huỷ bỏ
            </button>
            <SaveButton saving={saving} isEdit={drawerMode === "edit"} />
          </div>
        </form>
      </Modal>

      {/* Confirm delete */}
      {confirm && (
        <ConfirmModal
          message="Xoá tài khoản này?"
          description="Hành động này không thể hoàn tác và sẽ xoá toàn bộ dữ liệu liên quan."
          onConfirm={() => handleDelete(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Toast portal */}
      <ToastPortal />
    </div>
  );
};

export default AdminUsers;
